import { apiUrl } from "./api";

/**
 * Google Analytics 4, switched on and off by the analytics cookie consent.
 *
 * The measurement IDs come from a platform admin (`/api/site-config`), not the
 * build, so changing them needs no redeploy. Only IDs are accepted there — this
 * module writes the gtag loader itself, so the setting can't inject script.
 *
 * Pages are reported by their route template (`/employees/$id`), never their
 * real URL: sign-in, reset, invitation and signing links carry tokens in the
 * path or query string, and an ID in a path is still a customer's data. That
 * is also why the automatic page view is off and `trackPageView` sends them.
 */

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

let idsPromise: Promise<string[]> | null = null;
let enabled = false;
let loaded = false;
/** The route last navigated to, sent when consent arrives mid-visit. */
let currentPath: string | null = null;

function fetchIds(): Promise<string[]> {
  idsPromise ??= fetch(apiUrl("/api/site-config"))
    .then((res) => (res.ok ? res.json() : null))
    .then((data: { googleAnalyticsIds?: unknown } | null) =>
      Array.isArray(data?.googleAnalyticsIds)
        ? data.googleAnalyticsIds.filter((id): id is string => typeof id === "string")
        : [],
    )
    // Analytics must never break the app; no config means no tag.
    .catch(() => []);
  return idsPromise;
}

function gtag(...args: unknown[]): void {
  window.gtag?.(...args);
}

function pageLocation(path: string): string {
  return `${window.location.origin}${path}`;
}

function load(ids: string[]): void {
  window.dataLayer = window.dataLayer ?? [];
  // gtag.js reads the `arguments` object itself, so this must stay a plain
  // function rather than an arrow pushing a rest array.
  window.gtag = function gtagShim() {
    // biome-ignore lint/complexity/noArguments: gtag.js requires the arguments object.
    window.dataLayer?.push(arguments);
  };
  gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  gtag("js", new Date());
  for (const id of ids) {
    gtag("config", id, {
      send_page_view: false,
      page_location: currentPath ? pageLocation(currentPath) : window.location.origin,
      // Keep the cookies on this host: consent was given to the app, not to
      // the marketing site on the parent domain.
      cookie_domain: window.location.hostname,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
  }

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ids[0] ?? "")}`;
  document.head.appendChild(script);
  loaded = true;
}

function setDisabled(ids: string[], disabled: boolean): void {
  for (const id of ids) {
    (window as unknown as Record<string, boolean>)[`ga-disable-${id}`] = disabled;
  }
}

/** Called when analytics consent is granted (every page load, and on change). */
export async function enableAnalytics(): Promise<void> {
  enabled = true;
  const ids = await fetchIds();
  // Consent may have been withdrawn while the IDs were loading.
  if (!enabled || ids.length === 0) return;
  setDisabled(ids, false);
  if (loaded) gtag("consent", "update", { analytics_storage: "granted" });
  else load(ids);
  if (currentPath) sendPageView(currentPath);
}

/** Called when analytics consent is withdrawn. Cookie removal is the caller's. */
export async function disableAnalytics(): Promise<void> {
  enabled = false;
  if (!loaded) return;
  const ids = await fetchIds();
  setDisabled(ids, true);
  gtag("consent", "update", { analytics_storage: "denied" });
}

/** Records a navigation to `routePath`, a route template such as `/employees/$id`. */
export function trackPageView(routePath: string): void {
  if (routePath === currentPath) return;
  currentPath = routePath;
  if (enabled && loaded) sendPageView(routePath);
}

function sendPageView(path: string): void {
  gtag("event", "page_view", {
    page_location: pageLocation(path),
    page_path: path,
    // The real title can name an employee or a document.
    page_title: path,
  });
}
